import { parseMp4AudioConfig } from './aac';
import { NoArgsVoidFunc, Nullable, noopFunc, arrayUnstrictShift } from './utils';
import { WebCodecsPlayerChunk } from './webcodecs-player';

const MICRO_UNIT = 1000000;
const SUPPORTED_AUDIO_FORMATS = [
  'f32-planar', 'f32'
];

export class WebCodecsAudioDecHandler {

  private _decoder: AudioDecoder;
  private _isConfigured = false;
  private _decConfig: Nullable<AudioDecoderConfig> = null;

  private _aCtx: AudioContext;
  private _aClockRefUs = NaN;
  private _gainNode: GainNode;
  private _destNode: AudioNode;

  private _dtsOffsetUs = NaN;

  private _currentDtsUs = NaN;
  private _lastDtsUs = NaN;

  private _timestampQueue: Array<number> = [];
  private _nextTimestamp = NaN;

  constructor(private _schedulingLatencySecs: number) {
    this._decoder = new AudioDecoder({
      output: this._onAudioDecOut.bind(this),
      error: this._onAudioDecError.bind(this)
    });

    /*
    latencyHint Optional

    The type of playback that the context will be used for, as a predefined string ("balanced", "interactive" or "playback") or a double-precision floating-point value indicating the preferred maximum latency of the context in seconds. The user agent may or may not choose to meet this request; check the value of AudioContext.baseLatency to determine the true latency after creating the context.

        "balanced": The browser balances audio output latency and power consumption when selecting a latency value.
        "interactive" (default value): The audio is involved in interactive elements, such as responding to user actions or needing to coincide with visual cues such as a video or game action. The browser selects the lowest possible latency that doesn't cause glitches in the audio. This is likely to require increased power consumption.
        "playback": The browser selects a latency that will maximize playback time by minimizing power consumption at the expense of latency. Useful for non-interactive playback, such as playing music.
    */
    this._aCtx =  new AudioContext({
      latencyHint: 'interactive'
    });
    this._gainNode = this._aCtx.createGain();
    this._gainNode.connect(this._aCtx.destination);
    this._gainNode.gain.value = 1;
    this._destNode = this._gainNode;
  }

  get currentTimeUs(): number {
    return this._currentDtsUs;
  }

  get latestTimeUs(): number {
    return this._lastDtsUs;
  }

  get gainParam() {
    return this._gainNode.gain;
  }

  get config() {
    return this._decConfig;
  }

  onBufferUnderrun: NoArgsVoidFunc = noopFunc;

  decode(chunk: WebCodecsPlayerChunk) {

    const {
      payloadUnit,
      dtsUs,
      isCodecConfig,
    } = chunk;

    if (isCodecConfig) {
      if (this._isConfigured) return;

      const mp4aConfig = payloadUnit;
      const {numberOfChannels, sampleRate} = parseMp4AudioConfig(mp4aConfig);
      const decoderConfig: AudioDecoderConfig = {
        codec: 'mp4a.40.2',
        numberOfChannels,
        sampleRate,
        description: payloadUnit
      }

      if (!AudioDecoder.isConfigSupported(decoderConfig)) {
        throw new Error(`AudioDecoder config not supported: ${JSON.stringify(decoderConfig)}`);
      }

      this._decoder.configure(decoderConfig);
      if(this._decoder.state !== 'configured') {
        throw new Error(`AudioDecoder configure failed with param: ${JSON.stringify(decoderConfig)}`);
      }
      this._isConfigured = true;
      this._decConfig = decoderConfig;

      return;
    }
    if (!this._isConfigured) return;

    const audioChunk = new EncodedAudioChunk({
      data: payloadUnit,
      timestamp: dtsUs,
      type: 'key'
    });

    //console.debug('AudioDecoder state:', this._decoder.state, this._decConfig);
    this._timestampQueue.push(audioChunk.timestamp);
    this._decoder.decode(audioChunk);
  }

  private _onAudioDecOut(rawAudio: AudioData) {
    if(!this._timestampQueue.length) {
      rawAudio.close();
      return;
    }

    /*
    // note: duration us also exists already as property of AudioData (but will be same value as computed here)
    const durationUs = (rawAudio.numberOfFrames * MICRO_UNIT) / rawAudio.sampleRate;
    const dtsUs = Math.round(Math.round(arrayUnstrictShift(this._timestampQueue) / durationUs) * durationUs);
    */

    let dtsUs: number;
    const durationUs = rawAudio.duration;
    const queueDtsUs = arrayUnstrictShift(this._timestampQueue);
    if (isNaN(this._nextTimestamp)) {
      if (queueDtsUs != rawAudio.timestamp) {
        throw new Error('Initial audio decoder output timestamp should match dequeued one');
      }
      dtsUs = queueDtsUs;
    } else {
      // detect gap
      if (queueDtsUs >= this._nextTimestamp + durationUs) {
        console.warn('audio gap')
        dtsUs = this._nextTimestamp + durationUs * Math.floor((queueDtsUs - this._nextTimestamp) / durationUs);
      } else {
        dtsUs = this._nextTimestamp;
      }
    }
    this._nextTimestamp = dtsUs + durationUs;

    const aBufSrcNode = this._loadAudioBufferSource(rawAudio, dtsUs);

    if (isNaN(this._dtsOffsetUs)) {
      this._dtsOffsetUs = dtsUs;
      // to make sure we compute DTS diffs with integers
      const aClockRefSecs = this._aCtx.currentTime + this._schedulingLatencySecs;
      // important: mult before round (otherwise heavy precision loss)
      this._aClockRefUs = Math.round(MICRO_UNIT * aClockRefSecs);
    }

    // fixme: check for discontinuities -> timestamp rollovers etc

    const startTimeUs = this._aClockRefUs + dtsUs - this._dtsOffsetUs;
    const startTimeSecs = startTimeUs / MICRO_UNIT;

    if (startTimeSecs <= this._aCtx.currentTime) {
      //console.debug(this._aCtx.baseLatency, this._aCtx.outputLatency)
      console.warn('WebAudio schedule: Too late buffer at:', dtsUs, '[us]', 'by a-clock delta:', this._aCtx.currentTime - startTimeSecs, '[s]');
      // TBD if still necessary
      //this._dtsOffsetUs = NaN;
      //this.onBufferUnderrun();
      aBufSrcNode.disconnect();
      // null the closure ref to make sure the node is GCd
      aBufSrcNode.onended = null;
      return;
    }

    this._lastDtsUs = dtsUs;

    aBufSrcNode.start(startTimeSecs);
  }

  /**
   * Closes the AudioData
   */
  private _loadAudioBufferSource(rawAudio: AudioData, dtsUs: number): AudioBufferSourceNode {
    const decConf = this._decConfig;
    if (!decConf) {
      throw new Error('Assertion failed: Decoder config not set');
    }
    const {numberOfChannels, sampleRate} = decConf;
    if (numberOfChannels !== rawAudio.numberOfChannels) {
      throw new Error('numberOfChannels of decoder output not corresponding to config: ' + rawAudio.numberOfChannels);
    }
    // fixme: with HE-AAC the advertised sample-rate can be half of configured
    // -> remove this assertion all together? or check for
    if (sampleRate !== rawAudio.sampleRate) {
      throw new Error('sampleRate of decoder output not corresponding to config: ' + rawAudio.sampleRate);
    }
    if(!WebCodecsAudioDecHandler._isAudioFormatSupported(rawAudio.format)) {
      throw new Error('Unexpected raw audio format: ' + rawAudio.format);
    }

    const {
      // what is called frames here is ambiguous to the AAC frame concept (always 1024 samples)
      numberOfFrames: numberOfSamples,
      sampleRate: outSampleRate,
      //timestamp,
      duration
    } = rawAudio;

    const aCtx = this._aCtx;

    const aBuf = aCtx.createBuffer(
      numberOfChannels,
      numberOfSamples,
      sampleRate, // use outSampleRate
    );

    // copying each channel data from WebCodec to WebAudio needs an intermediate swap array
    if('f32-planar' === rawAudio.format) {
      for (let i = 0; i < numberOfChannels; i++) {
        const chanPlaneData = new Float32Array(numberOfSamples);
        rawAudio.copyTo(chanPlaneData, { planeIndex: i });
        aBuf.copyToChannel(chanPlaneData, i);
      }
    } else if('f32' === rawAudio.format) {
      const interleavedData = new Float32Array(numberOfSamples*numberOfChannels);
      rawAudio.copyTo(interleavedData, { planeIndex: 0 });
      for (let i = 0; i < numberOfChannels; i++) {
        const chanPlaneData = aBuf.getChannelData(i);
        for (let k = 0; k < numberOfSamples; k++) {
          chanPlaneData[k] = interleavedData[k*numberOfChannels+i];
        }
      }
    }
    // release input data, we dont need it anymore
    rawAudio.close();

    let bufSrcNode: Nullable<AudioBufferSourceNode> = aCtx.createBufferSource();
    bufSrcNode.buffer = aBuf;
    bufSrcNode.loop = false;
    bufSrcNode.connect(this._destNode);
    bufSrcNode.onended = () => {
      if (!bufSrcNode) return;
      this._currentDtsUs = dtsUs + duration;
      bufSrcNode.disconnect();
      // null the closure ref to make sure the node is GCd
      bufSrcNode = null;
    }

    return bufSrcNode;
  }

  private _onAudioDecError(err: Error) {
    console.error('AudioDecoder error:', err);
    throw err;
  }

  private static _isAudioFormatSupported(format: string) {
    return (SUPPORTED_AUDIO_FORMATS.indexOf(format) > -1);
  }
}
